import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../api/endpoints';
import { useAuthStore } from '../stores/auth.store';
import { portal } from '../App';
import type { LoginRequest, Account, Shop } from '@motra/shared';

function portalLanding(): string {
  switch (portal) {
    case 'manager':
      return '/dashboard';
    case 'vault':
      return '/vault';
    case 'distributor':
      return '/inventory';
    default:
      return '/pos';
  }
}

export function useLogin() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);

  return useMutation({
    mutationFn: (data: LoginRequest) => authApi.login(data),
    onSuccess: (res: any) => {
      // API returns { status: "ok", token, user, account, shop }
      // or wrapped as { success: true, data: { token, ... } }
      const payload = res.data ?? res;
      const token = payload.token;
      const user = payload.user;
      const account = payload.account;
      const shop = payload.shop;

      if (token && user) {
        setAuth({
          token,
          user: user as Parameters<typeof setAuth>[0]['user'],
          account: account as Account,
          shop: (shop as Shop) ?? null,
        });
        navigate(portalLanding());
      }
    },
  });
}

export function useLogout() {
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authApi.logout(),
    onSettled: () => {
      logout();
      queryClient.clear();
      navigate('/login');
    },
  });
}

export function useVerify() {
  const token = useAuthStore((s) => s.token);

  return useQuery({
    queryKey: ['auth', 'verify'],
    queryFn: () => authApi.verify(),
    enabled: !!token,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });
}

export function useInitialize() {
  const shop = useAuthStore((s) => s.shop);
  const setShop = useAuthStore((s) => s.setShop);
  const token = useAuthStore((s) => s.token);

  return useQuery({
    queryKey: ['auth', 'initialize', shop?.id],
    queryFn: async () => {
      if (!shop?.id) return null;
      const res = await authApi.initialize(shop.id);
      if (res.success && res.data?.shop) {
        setShop(res.data.shop as Shop);
      }
      return res.data ?? null;
    },
    enabled: !!token && !!shop?.id,
    staleTime: 1000 * 60 * 10,
  });
}
